import { useState, useMemo } from 'react';
import { type ExpandedEvent } from '../lib/rrule';
import { useCalendarPage } from '../contexts/CalendarPageContext';
import { startOfDay } from '../lib/dateUtils';
import EventPanel from './EventPanel';

interface MobileDayViewProps {
  selectedDate: Date;
  onEventCreated?: () => void;
}

const DAYS = [
  'Sun',
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export default function MobileDayView({
  selectedDate,
  onEventCreated,
}: MobileDayViewProps) {
  const {
    calendarId,
    expandedEvents,
    calendarColorMap,
    calColor,
    holidaysByDay,
    refetchEvents,
    editEventId,
    editOccurrenceDate,
    createForDate,
    setCreateForDate,
    selectEventForEdit,
    closePanel,
    closeDayModal,
  } = useCalendarPage();

  const [displayDate, setDisplayDate] =
    useState(selectedDate);

  // Build 5-day strip (selected ± 2 days)
  const dayStrip = useMemo(() => {
    const days: Date[] = [];
    for (let i = -2; i <= 2; i++) {
      const d = new Date(displayDate);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [displayDate]);

  // Get events for a specific day (for the strip dots)
  function getDayEvents(date: Date): ExpandedEvent[] {
    const y = date.getFullYear();
    const m = date.getMonth();
    const d = date.getDate();
    const pad = (n: number) => String(n).padStart(2, '0');
    const targetDateStr = `${y}-${pad(m + 1)}-${pad(d)}`;
    return expandedEvents.filter(e => {
      // ── All-day events: timezone-free, compare YYYY-MM-DD strings ──
      if (e.all_day && e.start_date) {
        const sDate = e.start_date.slice(0, 10);
        const eDate = e.end_date?.slice(0, 10);
        // Multi-day all-day events: check if target date falls within range
        if (eDate && sDate !== eDate) {
          return (
            targetDateStr >= sDate && targetDateStr <= eDate
          );
        }
        // Single-day all-day event: match start_date exactly
        return sDate === targetDateStr;
      }
      // ── Timed events: stored in UTC, compare local date components ──
      const start = new Date(e.start_at);
      return (
        start.getFullYear() === y &&
        start.getMonth() === m &&
        start.getDate() === d
      );
    });
  }

  // Get multi-day events that span across a given day (but didn't start on it)
  function getSpanningEvents(date: Date): ExpandedEvent[] {
    const MS_PER_DAY = 86400000;
    const target = startOfDay(date);
    return expandedEvents.filter(e => {
      let eStart: Date;
      let eEnd: Date;
      if (e.all_day && e.start_date && e.end_date) {
        if (e.start_date === e.end_date) return false;
        eStart = startOfDay(
          new Date(e.start_date + 'T12:00')
        );
        eEnd = startOfDay(new Date(e.end_date + 'T12:00'));
      } else {
        const dur =
          new Date(e.end_at).getTime() -
          new Date(e.start_at).getTime();
        if (dur <= MS_PER_DAY) return false;
        eStart = startOfDay(new Date(e.start_at));
        eEnd = startOfDay(new Date(e.end_at));
      }
      return (
        eStart <= target &&
        eEnd >= target &&
        !isSameDay(eStart, target)
      );
    });
  }

  // Get events for display date (sorted), including spanning multi-day events
  const displayDayEvents = useMemo(() => {
    const dayEvents = getDayEvents(displayDate);
    const spanning = getSpanningEvents(displayDate).filter(
      e => !dayEvents.some(o => o.id === e.id)
    );
    const allDay = [
      ...dayEvents.filter(e => e.all_day),
      ...spanning.filter(e => e.all_day),
    ];
    const timed = [
      ...dayEvents.filter(e => !e.all_day),
      ...spanning.filter(e => !e.all_day),
    ].sort(
      (a, b) =>
        new Date(a.start_at).getTime() -
        new Date(b.start_at).getTime()
    );
    return { allDay, timed };
  }, [expandedEvents, displayDate]);

  console.log('[MobileDayView]', {
    displayDate,
    expandedEventsCount: expandedEvents.length,
    allDayCount: displayDayEvents.allDay.length,
    timedCount: displayDayEvents.timed.length,
    events: [
      ...displayDayEvents.allDay,
      ...displayDayEvents.timed,
    ].map(e => ({
      id: e.id,
      title: e.title,
      all_day: e.all_day,
      start_date: e.start_date,
      end_date: e.end_date,
      start_at: e.start_at,
    })),
  });

  function handleDayClick(date: Date) {
    setDisplayDate(date);
    closePanel();
  }

  function handleAddEvent() {
    setCreateForDate(displayDate);
  }

  function handleEventClick(event: ExpandedEvent) {
    selectEventForEdit(event);
  }

  function handleBack() {
    closePanel();
  }

  function handleEventPanelClose() {
    refetchEvents();
    onEventCreated?.();
    closePanel();
  }

  const isEventsPanel = !editEventId && !createForDate;
  const isFormPanel =
    Boolean(editEventId) || Boolean(createForDate);

  return (
    <div className="fixed inset-0 z-50 bg-paper-50 flex flex-col">
      {/* Header with 5-day strip */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-paper-300 bg-paper-100 shrink-0">
        <div className="flex items-center gap-2">
          {isFormPanel && (
            <button
              onClick={handleBack}
              aria-label="Back"
              className="text-ink-400 hover:text-ink-700 transition-colors text-lg leading-none"
            >
              ←
            </button>
          )}
          <span
            className="w-2.5 h-2.5 rounded-sm shrink-0"
            style={{ backgroundColor: calColor }}
          />
          <h2 className="font-serif text-lg font-bold text-ink-900">
            {isEventsPanel
              ? `${DAYS[displayDate.getDay()]}, ${MONTHS[displayDate.getMonth()]} ${displayDate.getDate()}`
              : createForDate
                ? 'New Event'
                : 'Edit Event'}
          </h2>
        </div>
        <button
          onClick={() => {
            closePanel();
            closeDayModal();
          }}
          aria-label="Close"
          className="text-ink-400 hover:text-ink-700 transition-colors text-lg leading-none"
        >
          ✕
        </button>
      </div>

      {/* 5-day strip */}
      <div className="flex items-center justify-center gap-2 px-4 py-3 border-b border-paper-300 bg-paper-50 shrink-0">
        {dayStrip.map((date, idx) => {
          const isSelected = isSameDay(date, displayDate);
          const dayEvents = [
            ...getDayEvents(date),
            ...getSpanningEvents(date).filter(
              e =>
                !getDayEvents(date).some(o => o.id === e.id)
            ),
          ];
          const colors = dayEvents.map(
            e => calendarColorMap[e.calendar_id] ?? calColor
          );
          const uniqueColors = [...new Set(colors)].slice(
            0,
            3
          );

          return (
            <button
              key={idx}
              onClick={() => handleDayClick(date)}
              className={[
                'flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors min-w-[52px]',
                isSelected
                  ? 'bg-paper-200 ring-2 ring-paper-300'
                  : 'hover:bg-paper-100',
              ].join(' ')}
            >
              <span className="font-mono text-[10px] text-ink-400 uppercase">
                {DAYS[date.getDay()]}
              </span>
              <span
                className={[
                  'font-mono text-lg font-semibold leading-none',
                  isSelected
                    ? 'text-ink-900'
                    : 'text-ink-600',
                ].join(' ')}
              >
                {date.getDate()}
              </span>
              {/* Event dots */}
              {uniqueColors.length > 0 && (
                <div className="flex gap-0.5 mt-0.5">
                  {uniqueColors.map((color, i) => (
                    <span
                      key={i}
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-y-auto">
        {isEventsPanel ? (
          <EventsList
            allDayEvents={displayDayEvents.allDay}
            timedEvents={displayDayEvents.timed}
            calendarColorMap={calendarColorMap}
            currentCalendarColor={calColor}
            holidays={
              holidaysByDay.get(displayDate.getDate()) ?? []
            }
            onEventClick={handleEventClick}
            onAddEvent={handleAddEvent}
          />
        ) : (
          <EventPanel
            embedded
            mode={editEventId ? 'edit' : 'create'}
            calendarId={calendarId}
            eventId={editEventId ?? undefined}
            occurrenceDate={editOccurrenceDate ?? undefined}
            defaultDate={createForDate ?? undefined}
            onClose={handleEventPanelClose}
          />
        )}
      </div>
    </div>
  );
}

// Sub-components

interface EventsListProps {
  allDayEvents: ExpandedEvent[];
  timedEvents: ExpandedEvent[];
  calendarColorMap: Record<string, string>;
  currentCalendarColor: string;
  holidays?: string[];
  onEventClick: (ev: ExpandedEvent) => void;
  onAddEvent: () => void;
}

function EventsList({
  allDayEvents,
  timedEvents,
  calendarColorMap,
  currentCalendarColor,
  holidays = [],
  onEventClick,
  onAddEvent,
}: EventsListProps) {
  return (
    <div className="flex flex-col">
      {/* Sticky Add Event Button */}
      <div className="sticky top-0 z-10 bg-paper-50 p-4 pb-3 border-b border-paper-200 shadow-sm">
        <button
          onClick={onAddEvent}
          className="w-full py-3 px-4 rounded-sm font-mono text-sm text-white hover:opacity-90 transition-opacity shadow-sm"
          style={{ backgroundColor: currentCalendarColor }}
        >
          + Add Event
        </button>
      </div>

      <div className="p-4 pt-3 space-y-5">
        {/* Holidays — pinned at top, unformatted */}
        {holidays.length > 0 && (
          <div className="space-y-1">
            {holidays.map((name, idx) => (
              <p
                key={`holiday-${idx}`}
                className="font-mono text-xs text-ink-400 italic"
              >
                🗓 {name}
              </p>
            ))}
          </div>
        )}

        {/* All-Day Events */}
        {allDayEvents.length > 0 && (
          <div>
            <h3 className="font-mono text-xs text-ink-400 uppercase tracking-widest mb-3">
              All-Day Events ({allDayEvents.length})
            </h3>
            <div className="space-y-2">
              {allDayEvents.map(ev => {
                const color =
                  (ev as { subscription_color?: string })
                    .subscription_color ??
                  calendarColorMap[ev.calendar_id] ??
                  currentCalendarColor;
                return (
                  <button
                    key={ev.id}
                    onClick={() => onEventClick(ev)}
                    className="w-full text-left px-3 py-2 rounded-sm text-white font-mono text-sm hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: color }}
                  >
                    {ev.title}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Timed Events */}
        {timedEvents.length > 0 && (
          <div>
            <h3 className="font-mono text-xs text-ink-400 uppercase tracking-widest mb-3">
              Events ({timedEvents.length})
            </h3>
            <div className="space-y-2">
              {timedEvents.map(ev => {
                const color =
                  (ev as { subscription_color?: string })
                    .subscription_color ??
                  calendarColorMap[ev.calendar_id] ??
                  currentCalendarColor;
                return (
                  <button
                    key={ev.id}
                    onClick={() => onEventClick(ev)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-sm bg-paper-100 hover:bg-paper-200 transition-colors text-left"
                  >
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <span className="font-mono text-xs text-ink-400 shrink-0 w-16">
                      {formatTime(ev.start_at)}
                    </span>
                    <span className="font-mono text-sm text-ink-800 truncate">
                      {ev.title}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {allDayEvents.length === 0 &&
          timedEvents.length === 0 &&
          holidays.length === 0 && (
            <div className="text-center py-12">
              <p className="font-mono text-sm text-ink-400">
                No events on this day
              </p>
              <p className="font-mono text-xs text-ink-300 mt-1">
                Tap "+ Add Event" to create one
              </p>
            </div>
          )}
      </div>
    </div>
  );
}
